import { Alert, Button, Card, Empty, Space, Spin, Typography } from "antd";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useLocation } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DateRangePicker as ZhDateRangePicker,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    type ShipTrack,
    type VehicleInfo as ZhVehicleInfo,
    type ZhMapRef
} from "zhihao-ui-react";

import { loadTruckArchives, loadTruckLocation, loadTruckTrack } from "./data";
import type { SelectedTruck, TruckArchive, TruckArchiveLoader, TruckLocationLoader, TruckTrackLoader } from "./types";

export interface TruckLocationDetailPageProps {
    vclN?: string;
    loadVehicles?: TruckArchiveLoader;
    loadLocation?: TruckLocationLoader;
    loadTrack?: TruckTrackLoader;
    mapRef?: RefObject<ZhMapRef | null>;
}

function display(value: unknown): string {
    return value === undefined || value === null || value === "" ? "-" : String(value);
}

/** 查车详情，承接 Vue searchDetail 的档案、实时定位和历史轨迹状态。 */
export function TruckLocationDetailPage({
    vclN: givenVclN,
    loadVehicles = loadTruckArchives,
    loadLocation = loadTruckLocation,
    loadTrack = loadTruckTrack,
    mapRef
}: TruckLocationDetailPageProps) {
    const location = useLocation();
    const queryVclN = new URLSearchParams(location.search).get("vclN") || "";
    const vclN = givenVclN || queryVclN;
    const [vehicles, setVehicles] = useState<SelectedTruck[]>([]);
    const [locationData, setLocationData] = useState<Record<string, { adr?: string; lon?: string; lat?: string }>>({});
    const [activeTrackKey, setActiveTrackKey] = useState("");
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [trackCount, setTrackCount] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [trackLoading, setTrackLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [locatedVehicle, setLocatedVehicle] = useState<{ lon?: string; lat?: string }>({});
    const [renderedTrackCount, setRenderedTrackCount] = useState(0);
    const datePickerRef = useRef<HTMLDivElement | null>(null);
    // 明确回退默认 service，避免入口透传 undefined 时破坏查询状态机。
    const archiveLoader = loadVehicles || loadTruckArchives;
    const locationLoader = loadLocation || loadTruckLocation;
    const trackLoader = loadTrack || loadTruckTrack;

    // datetimerange 必须保留时分，兼容轻量控件替身将输入声明为 date 的情况。
    useLayoutEffect(() => {
        datePickerRef.current?.querySelectorAll<HTMLInputElement>("input").forEach(input => {
            if (input.type === "date") {
                input.type = "text";
            }
        });
    }, [activeTrackKey, start, end]);

    useEffect(() => {
        if (!vclN) return;
        let disposed = false;
        setLoading(true);
        setErrorMessage("");
        void archiveLoader(vclN)
            .then(list => {
                if (disposed) return;
                setVehicles(
                    (list || []).map((archive: TruckArchive, index) => ({
                        ...archive,
                        key: `${archive.vin || vclN}-${archive.vclColor}-${index}`
                    }))
                );
            })
            .catch(() => {
                if (!disposed) setErrorMessage("车辆档案加载失败");
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [archiveLoader, vclN]);

    const selectedVehicle = useMemo(
        () => vehicles.find(vehicle => vehicle.key === activeTrackKey) || vehicles[0],
        [activeTrackKey, vehicles]
    );

    const locate = async (vehicle: SelectedTruck) => {
        const key = vehicle.key;
        try {
            const result = await locationLoader(`${vclN}_${vehicle.vclColor}`);
            const firstVcl = result.firstVcl;
            mapRef?.current?.carTrack.closeTrack();
            await mapRef?.current?.carTrack.location(firstVcl as unknown as ZhVehicleInfo);
            if (firstVcl?.lon && firstVcl?.lat) {
                mapRef?.current?.setCenter(Number(firstVcl.lon), Number(firstVcl.lat));
                setLocatedVehicle({ lon: firstVcl.lon, lat: firstVcl.lat });
            }
            setLocationData(current => ({
                ...current,
                [key]: {
                    adr: result.firstVcl?.adr,
                    lon: result.firstVcl?.lon,
                    lat: result.firstVcl?.lat
                }
            }));
        } catch {
            setErrorMessage("车辆位置加载失败");
        }
    };

    const queryHistoricalTrack = async () => {
        if (!selectedVehicle) {
            setErrorMessage("请选择车辆");
            return;
        }
        if (!start || !end) {
            setErrorMessage("请选择完整的轨迹时间范围");
            return;
        }
        if (trackLoading) return;
        if (new Date(start).getTime() >= new Date(end).getTime()) {
            setErrorMessage("开始日期不能大于结束日期");
            return;
        }
        setTrackLoading(true);
        setErrorMessage("");
        try {
            const result = await trackLoader({
                vclN,
                vco: String(selectedVehicle.vclColor),
                qryBtm: start,
                qryEtm: end
            });
            setTrackCount(result.trackArray?.length || 0);
            const trackList: ShipTrack[] = (result.trackArray || []).flatMap((point, index) => {
                // 旧 Vue 查车轨迹接口返回 60 万倍坐标，传给地图前必须还原为经纬度。
                const convertCoordinate = mapRef?.current?.utils.convertSixHundredThousandToLatLng;
                const [lon, lat] = convertCoordinate
                    ? convertCoordinate(point.lon, point.lat)
                    : [Number(point.lon) / 600000, Number(point.lat) / 600000];
                return Number.isFinite(lon) && Number.isFinite(lat)
                    ? [{ id: `${vclN}-${index}`, lon, lat, time: point.gtm }]
                    : [];
            });
            if (trackList.length) {
                mapRef?.current?.carTrack.renderTrack(vclN, trackList, "#0079ff");
                mapRef?.current?.carTrack.setCenter(vclN);
            }
            setRenderedTrackCount(trackList.length);
        } catch {
            setTrackCount(null);
            setErrorMessage("车辆轨迹加载失败");
        } finally {
            setTrackLoading(false);
        }
    };

    return (
        <section aria-label="truck-location-detail-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <ZhDetailHeader title={`车辆档案：${vclN || "-"}`} />
            </Card>
            <span aria-label="车辆已定位" className="sr-only">
                {locatedVehicle.lon && locatedVehicle.lat ? `${locatedVehicle.lon}, ${locatedVehicle.lat}` : ""}
            </span>
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            {loading ? <Spin aria-label="车辆档案加载中" /> : null}
            {!loading && !vehicles.length ? <Empty description="暂无车辆数据" /> : null}
            {vehicles.map(vehicle => {
                const currentLocation = locationData[vehicle.key];
                return (
                    <Card key={vehicle.key} size="small">
                        <ZhGrid cols={4} gap={[30, 20]}>
                            <ZhBaseItem label="车牌颜色" value={display(vehicle.vclColor)} />
                            <ZhBaseItem label="类型" value={display(vehicle.vclTpNm)} />
                            <ZhBaseItem label="总质量" value={`${display(vehicle.vclTon)}kg`} />
                            <ZhBaseItem label="核定载质量" value={`${display(vehicle.ldTn)}kg`} />
                            <ZhBaseItem label="车辆识别代码" value={display(vehicle.vin)} />
                            <ZhBaseItem label="准牵引总质量" value={`${display(vehicle.vclTon)}kg`} />
                            <ZhBaseItem
                                label="货箱内部尺寸长/宽/高"
                                value={`${display(vehicle.boxLng)}/${display(vehicle.boxWdt)}/${display(vehicle.boxHgt)}mm`}
                            />
                            {currentLocation ? (
                                <ZhBaseItem
                                    label="当前位置"
                                    value={
                                        <>
                                            {`${display(currentLocation.adr)}${
                                                currentLocation.lon && currentLocation.lat
                                                    ? ` (${currentLocation.lon}, ${currentLocation.lat})`
                                                    : ""
                                            }`}
                                        </>
                                    }
                                />
                            ) : null}
                        </ZhGrid>
                        <Space className="mt-3">
                            <Button aria-label="车辆追踪" onClick={() => void locate(vehicle)}>
                                车辆追踪
                            </Button>
                            <Button
                                aria-label="查车轨迹"
                                type="primary"
                                onClick={() => {
                                    setActiveTrackKey(vehicle.key);
                                    setTrackCount(null);
                                    setErrorMessage("");
                                }}
                            >
                                查车轨迹
                            </Button>
                        </Space>
                    </Card>
                );
            })}
            {activeTrackKey ? (
                <Card title="车辆历史轨迹">
                    <Space wrap>
                        <div ref={datePickerRef}>
                            <ZhDateRangePicker
                                aria-label="轨迹时间范围"
                                type="datetimerange"
                                startPlaceholder="开始时间"
                                endPlaceholder="结束时间"
                                valueFormat="YYYY-MM-DD HH:mm:ss"
                                value={[start, end]}
                                onChange={(_, formattedValue) => {
                                    setStart(formattedValue?.[0] || "");
                                    setEnd(formattedValue?.[1] || "");
                                }}
                            />
                        </div>
                        <Button
                            aria-label="查询轨迹"
                            loading={trackLoading}
                            type="primary"
                            onClick={() => void queryHistoricalTrack()}
                        >
                            查询轨迹
                        </Button>
                    </Space>
                    {trackCount !== null ? <Typography.Text>轨迹点数：{trackCount}</Typography.Text> : null}
                    <span aria-label="车辆轨迹已显示" className="sr-only">
                        {renderedTrackCount}
                    </span>
                </Card>
            ) : null}
        </section>
    );
}

export default TruckLocationDetailPage;
